import { useEffect, useRef, useState } from "react"
import {
	IconCheckOutline18 as CheckIcon,
	IconCopyOutline18 as CopyIcon,
	IconPlusOutline18 as PlusIcon,
	IconXmarkOutline18 as XmarkIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { IconButton, Tooltip } from "@productlane/ui"
import { useCopiedFlag } from "@productlane/ui/src/hooks/useCopiedFlag"

import type { PanelGroup } from "./use-panel-group"

interface PanelBarProps extends PanelGroup {
	addLabel: string
	removeLabel: string
	onAdd: () => void
	onRemove: (index: number) => void
	onCopy?: () => string
}

export function PanelBar({
	panels,
	active,
	select,
	rename,
	addLabel,
	removeLabel,
	onAdd,
	onRemove,
	onCopy,
}: PanelBarProps) {
	const [editing, setEditing] = useState<number | null>(null)
	const [draft, setDraft] = useState("")
	const [copied, markCopied] = useCopiedFlag(2000)
	const inputRef = useRef<HTMLInputElement | null>(null)

	useEffect(() => {
		if (editing !== null) inputRef.current?.select()
	}, [editing])

	const startRename = (index: number, label: string) => {
		setDraft(label)
		setEditing(index)
	}

	const commit = () => {
		if (editing !== null) rename(editing, draft)
		setEditing(null)
	}

	return (
		<div
			className="docs-panel-bar"
			role="tablist"
			contentEditable={false}
			suppressContentEditableWarning
		>
			{panels.map((panel, index) => {
				const position = index + 1
				const isActive = position === active
				return (
					<div
						key={panel.key}
						role="tab"
						aria-selected={isActive}
						// Roving tabindex: the selected label is the one stop the bar
						// takes, the way a tablist is meant to behave.
						tabIndex={isActive ? 0 : -1}
						className="docs-panel-tab group/tab cursor-default"
						onClick={() => select(position)}
						onDoubleClick={() => startRename(position, panel.label)}
						onKeyDown={(event) => {
							if (event.key !== "Enter" && event.key !== " ") return
							if (editing === position) return
							event.preventDefault()
							select(position)
						}}
					>
						{editing === position ? (
							<input
								ref={inputRef}
								value={draft}
								onChange={(event) => setDraft(event.target.value)}
								onBlur={commit}
								onKeyDown={(event) => {
									if (event.key === "Enter") {
										event.preventDefault()
										commit()
									}
									if (event.key === "Escape") setEditing(null)
								}}
								className="w-24 min-w-0 bg-transparent text-sm font-medium text-primary outline-none"
							/>
						) : (
							<span className="docs-panel-tab-label">{panel.label}</span>
						)}
						{/* Mounted for every label so switching tabs moves nothing. */}
						<Tooltip label={removeLabel}>
							<IconButton
								variant="ghost"
								size="sm"
								Icon={XmarkIcon}
								aria-label={removeLabel}
								tabIndex={isActive ? 0 : -1}
								className={cn(
									"h-4 w-4 flex-none cursor-default opacity-0 group-hover/tab:opacity-100",
									panels.length < 2 && "invisible",
								)}
								onClick={(event) => {
									event.stopPropagation()
									onRemove(position)
									select(Math.max(1, position - 1))
								}}
							/>
						</Tooltip>
					</div>
				)
			})}
			<span className="docs-panel-add">
				<Tooltip label={addLabel}>
					<IconButton
						variant="ghost"
						size="sm"
						Icon={PlusIcon}
						aria-label={addLabel}
						className="h-4 w-4 flex-none cursor-default"
						onClick={onAdd}
					/>
				</Tooltip>
			</span>
			{onCopy && (
				<span className="docs-panel-copy-slot">
					<Tooltip label={copied ? "Copied" : "Copy code"}>
						<IconButton
							variant="ghost"
							size="sm"
							Icon={copied ? CheckIcon : CopyIcon}
							aria-label={copied ? "Copied" : "Copy code"}
							className="h-4 w-4 flex-none cursor-default"
							onClick={() => {
								void navigator.clipboard?.writeText(onCopy()).then(() => {
									markCopied()
								})
							}}
						/>
					</Tooltip>
				</span>
			)}
		</div>
	)
}
